import React, { useMemo, useState } from 'react';
import Head from 'next/head';
import { gql } from '@apollo/client';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { client, getPostByURI } from '../../../apollo-client';
import { useAuth0User } from '@hooks/useAuth0User';
import { useAccessToken } from '@hooks/useAccessToken';
import useLoggedOutRedirect from '@hooks/useLoggedOutRedirect';
import {
  getYoastHeadSEO,
  ROLE_LEVELS,
  hasSufficientPrivilegesWithNewUserRestrictions
} from '@lib/utils';
import { useIsAppView } from '@hooks/useIsAppView';
import WideLayout from '@components/layout/wide';
import { GAMEWEEK_ONE, MAX_GAMEWEEK, TEN_MINUTES_IN_MS } from '@lib/constants';
import Paywall from '@components/paywall';
import LoadingView from '@components/atoms/loading-view';
import SegmentedControl from '@components/atoms/segmented-control';
import EndOfSeasonHoldingMessage from '@components/end-of-season-holding-message';
import ErrorHoldingMessage from '@components/error-holding-message';
import { ffhClient } from '@lib/api/ffh-client';
import { authHeader } from '@lib/api/auth-header';
import { useLeagueContext } from '@hooks/useLeagueContext';
import TeamLineup from '@components/team-lineup';
import SpiderChart from '@components/spider-chart';
import {
  AI_TEAM_SPIDER_AXES,
  AI_TEAM_SPIDER_TICK_STEP,
  buildAiTeamSpiderCaption,
  buildAiTeamSpiderSeries,
  resolveAiTeamScaleMaximum,
  startingElevenPoints
} from '@lib/page-utils/ai-teams/spider';
import { usePlannerState } from '@hooks/usePlannerState';
import { theme } from 'tailwind.config';

type RecommendationParams = {
  season: number;
  gameweek: number;
  accessToken: string;
};

class RecommendationError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'RecommendationError';
    this.status = status;
  }
}

const fetchFreehit = async ({
  season,
  gameweek,
  accessToken
}: RecommendationParams) => {
  const { data, error, response } = await ffhClient().GET(
    '/recommendations/teams/freehit',
    {
      headers: authHeader(accessToken),
      params: { query: { season, gameweek } }
    }
  );
  if (error)
    throw new RecommendationError(
      'Failed to load freehit recommendation',
      response?.status
    );
  return data?.data ?? null;
};

const fetchWildcard = async ({
  season,
  gameweek,
  accessToken
}: RecommendationParams) => {
  const { data, error, response } = await ffhClient().GET(
    '/recommendations/teams/wildcard',
    {
      headers: authHeader(accessToken),
      params: { query: { season, gameweek } }
    }
  );
  if (error)
    throw new RecommendationError(
      'Failed to load wildcard recommendation',
      response?.status
    );
  return data?.data ?? null;
};

const BestAITeams = ({ post }) => {
  const router = useRouter();
  const { user: session, isLoading: userIsLoading } = useAuth0User();
  const { accessToken, accessTokenIsFetching } = useAccessToken();
  const { pathname } = router;
  const { isAppView } = useIsAppView();

  const { isRedirecting } = useLoggedOutRedirect({
    router,
    redirectPath: 'ai-team-rating',
    user: session,
    userIsLoading,
    mobileOnly: true
  });
  // null until the user picks a tab, so the default can depend on the league
  // context (wildcard for GW1) once it loads.
  const [selectedTab, setSelectedTab] = useState<number | null>(null);

  const isPayingMember = useMemo(
    () =>
      hasSufficientPrivilegesWithNewUserRestrictions(session, ROLE_LEVELS.PRO),
    [session]
  );

  const yoastHead = getYoastHeadSEO(post?.seo?.fullHead, pathname);

  const {
    context: leagueContext,
    isLoading: leagueContextIsLoading,
    isError: leagueContextIsError,
    refetch: refetchLeagueContext
  } = useLeagueContext({
    enabled: isPayingMember && !!accessToken,
    staleTime: TEN_MINUTES_IN_MS,
    refetchOnWindowFocus: false
  });

  const season = leagueContext?.season ?? 0;
  const nextGameweek = leagueContext?.nextGameweek?.number ?? 0;
  const currentGameweek = leagueContext?.currentGameweek?.number ?? 0;
  const isGameweekOne = nextGameweek === GAMEWEEK_ONE;
  const isFinalGameweek = nextGameweek === MAX_GAMEWEEK;
  const isSeasonOver = currentGameweek === MAX_GAMEWEEK;
  const hasContext = !!season && nextGameweek > 0;

  const {
    data: bestFreeHitTeam,
    isLoading: freehitIsLoading,
    isError: freehitIsError,
    error: freehitError,
    isFetching: freehitIsFetching,
    refetch: refetchFreehit
  } = useQuery({
    queryKey: ['ffh-freehit', season, nextGameweek],
    queryFn: () =>
      fetchFreehit({
        season,
        gameweek: nextGameweek,
        accessToken: accessToken as string
      }),
    enabled: isPayingMember && !!accessToken && hasContext && !isSeasonOver,
    refetchOnWindowFocus: false,
    refetchInterval: TEN_MINUTES_IN_MS
  });

  const {
    data: bestWildcardTeam,
    isLoading: wildcardIsLoading,
    isError: wildcardIsError,
    error: wildcardError,
    isFetching: wildcardIsFetching,
    refetch: refetchWildcard
  } = useQuery({
    queryKey: ['ffh-wildcard', season, nextGameweek],
    queryFn: () =>
      fetchWildcard({
        season,
        gameweek: nextGameweek,
        accessToken: accessToken as string
      }),
    enabled:
      isPayingMember &&
      !!accessToken &&
      hasContext &&
      !isSeasonOver &&
      !isFinalGameweek,
    refetchOnWindowFocus: false,
    refetchInterval: TEN_MINUTES_IN_MS
  });

  // Cover the cold-load window where the access token and league context
  // are still resolving. Without this, the recommendation queries stay
  // disabled (they need `hasContext`) and the page renders with no loader
  // and no team for a paying member until the context fetch lands.
  const isDataLoading =
    isPayingMember &&
    (accessTokenIsFetching ||
      leagueContextIsLoading ||
      !hasContext ||
      (!isFinalGameweek && wildcardIsLoading) ||
      freehitIsLoading);

  // `activeTab` maps directly to the segmented control tab index: 0 is free
  // hit, 1 is wildcard. The free hit chip can't be played in GW1, so the
  // wildcard team is the default there (FFH-2062, GW1 only); every other
  // gameweek defaults to free hit. Free hit is always available; wildcard is
  // hidden and its query disabled on the final gameweek, so force free hit
  // there regardless of any stale tab value.
  const defaultTab = isGameweekOne ? 1 : 0;
  const activeTab = selectedTab ?? defaultTab;
  const showFreehitTab = isFinalGameweek || activeTab === 0;
  const teamToShow = showFreehitTab ? bestFreeHitTeam : bestWildcardTeam;

  const headerLabel = showFreehitTab
    ? `Best Gameweek ${nextGameweek || GAMEWEEK_ONE} Free Hit Team`
    : `Best Gameweek ${nextGameweek || GAMEWEEK_ONE} Wildcard Team`;

  // The user's own squad for the same gameweek, so the recommendation can be
  // read against what they actually hold rather than in isolation.
  const { data: plannerState, hasTeam } = usePlannerState({
    enabled: isPayingMember && Boolean(accessToken)
  });
  const yourSquad = useMemo(() => {
    const gameweeks = plannerState?.data.gameweeks ?? [];
    return (
      gameweeks.find((gameweek) => gameweek.gameweek === nextGameweek) ??
      gameweeks[0] ??
      null
    );
  }, [plannerState, nextGameweek]);

  const spiderGameweek = nextGameweek || GAMEWEEK_ONE;
  const aiTeamLabel = showFreehitTab ? 'AI free hit' : 'AI wildcard';

  const spiderSeries = useMemo(() => {
    if (!teamToShow) return [];
    return buildAiTeamSpiderSeries(
      [
        {
          key: 'ai',
          label: aiTeamLabel,
          color: theme.colors.primary.DEFAULT,
          squad: teamToShow
        },
        ...(yourSquad
          ? [
              {
                key: 'yours',
                label: 'Your team',
                color: theme.colors.danger.DEFAULT,
                squad: yourSquad
              }
            ]
          : [])
      ],
      spiderGameweek
    );
  }, [teamToShow, aiTeamLabel, yourSquad, spiderGameweek]);

  const spiderCaption = teamToShow
    ? buildAiTeamSpiderCaption({
        gameweek: spiderGameweek,
        aiLabel: aiTeamLabel,
        aiPoints: startingElevenPoints(teamToShow, spiderGameweek),
        yourPoints: yourSquad
          ? startingElevenPoints(yourSquad, spiderGameweek)
          : null
      })
    : '';

  const recommendationError = showFreehitTab ? freehitError : wildcardError;
  const errorStatusCode =
    recommendationError instanceof RecommendationError
      ? recommendationError.status
      : undefined;
  const recommendationIsRetrying = showFreehitTab
    ? freehitIsFetching
    : wildcardIsFetching;

  const handleRecommendationRetry = () => {
    refetchFreehit();
    // `refetch()` runs even when a query is disabled, so skip the wildcard
    // refetch on the final gameweek where its query is intentionally off.
    if (!isFinalGameweek) {
      refetchWildcard();
    }
  };

  // A resolved-but-empty response (data null) leaves nothing to render just like
  // a hard failure, so both surface the recoverable error state rather than the
  // blank page paying members used to hit.
  const shouldShowRecommendationError =
    !isDataLoading &&
    ((showFreehitTab ? freehitIsError : wildcardIsError) || !teamToShow);

  // Logged-out mobile web visitors are being pushed to the GYTR flow; show a
  // loader instead of flashing the paywall or holding message mid-navigation.
  if (isRedirecting) {
    return (
      <>
        <Head>{yoastHead}</Head>
        <WideLayout className="bg-white" content={false}>
          <div className="flex justify-center h-[calc(100vh-175px)] bg-white">
            <LoadingView
              headerText="Loading"
              loadingText="Taking you to AI Team Rating..."
              progressBarSeconds={8}
            />
          </div>
        </WideLayout>
      </>
    );
  }

  if (currentGameweek === MAX_GAMEWEEK) {
    return (
      <>
        <Head>{yoastHead}</Head>
        <EndOfSeasonHoldingMessage pageTitle="AI Teams" isAppView={isAppView} />
      </>
    );
  }

  if (!isPayingMember) {
    return (
      <>
        <Head>
          {!yoastHead ? (
            <title>
              My Team FPL - Use Artificial Intelligence To Win at Fantasy
              Football
            </title>
          ) : (
            yoastHead
          )}
        </Head>
        <WideLayout className="bg-white" content={false}>
          <div className="h-screen pt-12 p-4">
            <Paywall variant="embedded" title="🔒 Unlock exclusive content" />
          </div>
        </WideLayout>
      </>
    );
  }

  if (leagueContextIsError) {
    return (
      <>
        <Head>{yoastHead}</Head>
        <WideLayout className="bg-white" content={false}>
          <ErrorHoldingMessage
            pageTitle="AI Teams"
            onRetry={refetchLeagueContext}
          />
        </WideLayout>
      </>
    );
  }

  return (
    <>
      <Head>
        {!yoastHead ? (
          <title>
            My Team FPL - Use Artificial Intelligence To Win at Fantasy Football
          </title>
        ) : (
          yoastHead
        )}
      </Head>
      <WideLayout className="bg-white" content={false}>
        {isDataLoading && (
          <div className="flex justify-center h-[calc(100vh-175px)] bg-white">
            <LoadingView
              headerText="Generating best AI teams"
              loadingText="AI analysing players..."
              progressBarSeconds={8}
            />
          </div>
        )}

        {shouldShowRecommendationError && (
          <ErrorHoldingMessage
            pageTitle="AI Teams"
            statusCode={errorStatusCode}
            onRetry={handleRecommendationRetry}
            isRetrying={recommendationIsRetrying}
          />
        )}

        {!isDataLoading && !shouldShowRecommendationError && teamToShow && (
          <div
            className={`mt-3 md:mt-8 bg-white pt-3 ${isAppView ? 'mb-4' : ''}`}
          >
            <div className="px-3 md:px-0 mb-5 md:mb-1">
              <SegmentedControl
                tabLabels={[
                  'AI Free Hit Team',
                  ...(isFinalGameweek ? [] : ['AI Wildcard Team'])
                ]}
                selectedTab={activeTab}
                setSelectedTab={setSelectedTab}
                dataCyValues={['ai-free-hit-button', 'ai-wildcard-button']}
              />
            </div>

            <div className="px-4 md:px-6 mt-6">
              <h2 className="text-2xl font-bold mb-2">{headerLabel}</h2>
              <p className="text-gray-700 mb-4">
                Predicted points:{' '}
                <strong>{teamToShow.predictedPoints?.toFixed(1) ?? '-'}</strong>
              </p>

              <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)] lg:items-start">
                <TeamLineup
                  starting={teamToShow.starting}
                  bench={teamToShow.bench}
                  maxFixtures={showFreehitTab ? 1 : 3}
                />

                {spiderSeries.length > 0 && (
                  <div className="shadow-100 p-5 bg-white rounded-lg mb-6 lg:mb-0">
                    <h3 className="text-lg font-bold mb-1">
                      {hasTeam ? 'Against your team' : 'Team shape'}
                    </h3>
                    <SpiderChart
                      className="mt-3"
                      chartClassName="h-[320px] sm:h-[360px]"
                      axes={AI_TEAM_SPIDER_AXES}
                      series={spiderSeries}
                      scaleMaximum={resolveAiTeamScaleMaximum(spiderSeries)}
                      tickStep={AI_TEAM_SPIDER_TICK_STEP}
                      scaleDescription={spiderCaption}
                    />
                    {!hasTeam && (
                      <p className="mt-2 mb-0 text-center text-xs text-neutral-600">
                        <Link href="/my-team/pick" className="underline">
                          Import your FPL team
                        </Link>{' '}
                        to see it on the chart.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </WideLayout>
    </>
  );
};

export default BestAITeams;

export const getStaticProps = async () => {
  const { data } = await client.query({
    query: gql`
      ${getPostByURI('/my-team-fpl-ai-best-teams')}
    `
  });

  const post = data.postBy;

  return {
    props: { post }
  };
};
